import {
    EmailOutlined,
    LocalOfferOutlined,
    LocationOnOutlined,
    PhoneOutlined,
    SvgIconComponent,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CardProps,
    Chip,
    Link,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import addressFormatter from '@fragaria/address-formatter';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@api/axiosInstance';
import Labels from './SelectLabel';
import LtrProvider from '@Components/Common/LtrProvider';

interface DetailItemProps {
    Icon: SvgIconComponent;
    children?: React.ReactNode;
}

const DetailItem: React.FC<DetailItemProps> = ({ Icon, isNull, children }) => {
    if (isNull) return null;
    return (
        <Box display="flex" gap={2}>
            <Icon fontSize="small" sx={{ mt: 0.25 }} />
            <Box display="flex" flexDirection="column" gap={0.5}>
                {children}
            </Box>
        </Box>
    );
};

interface ContactInfoCardProps extends CardProps {
    contact: any;
}

const ContactInfoCard = ({ contact, sx, ...props }: ContactInfoCardProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const address = addressFormatter.format({
        street: contact.street,
        houseNumber: contact.house_number,
        city: contact.city,
        country: contact.country?.toCountryDisplayName(),
        state: contact.state,
        neighbourhood: contact.neighbourhood,
        postcode: contact.zip_code,
    });

    const invalidateContact = () =>
        queryClient.invalidateQueries(['contact', contact.contact_id]);

    const { mutate: addLabel } = useMutation({
        mutationFn: label =>
            axiosInstance.post('contacts/labels/add', {
                contact_ids: [contact.contact_id],
                label_name: label.name,
            }),
        onSuccess: invalidateContact,
    });

    const { mutate: removeLabel } = useMutation({
        mutationFn: label =>
            axiosInstance.post('contacts/labels/remove', {
                contact_ids: [contact.contact_id],
                label_name: label.name,
            }),
        onSuccess: invalidateContact,
    });

    return (
        <Card
            elevation={0}
            sx={{
                mx: 0,
                ...sx,
            }}
            {...props}
        >
            <CardHeader
                title={t('Contact Details')}
                subheader={
                    <>
                        {contact.created && (
                            <>
                                {t('Added')}:&nbsp;
                                {new Date(contact.created).toLocaleDateString()}
                                <br />
                            </>
                        )}
                        {contact.updated && (
                            <>
                                {t('Edited')}:&nbsp;
                                {new Date(contact.updated).toLocaleDateString()}
                            </>
                        )}
                    </>
                }
            />
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Typography variant="bodyLarge">
                    {contact.description}
                </Typography>
                <DetailItem
                    Icon={EmailOutlined}
                    isNull={!contact.email_addresses.length}
                >
                    {contact.email_addresses.map(({ email }) => (
                        <LtrProvider key={email}>
                            <Link key={email} href={`mailto:${email}`}>
                                {email}
                            </Link>
                        </LtrProvider>
                    ))}
                </DetailItem>
                <DetailItem
                    Icon={PhoneOutlined}
                    isNull={!contact.phone_numbers.length}
                >
                    {contact.phone_numbers.map(({ phone_number }) => (
                        <LtrProvider key={phone_number}>
                            <Link
                                key={phone_number}
                                href={`tel:${phone_number}`}
                            >
                                {phone_number}
                            </Link>
                        </LtrProvider>
                    ))}
                </DetailItem>
                <DetailItem Icon={LocationOnOutlined} isNull={!address.trim()}>
                    <Typography>{address}</Typography>
                </DetailItem>
                <DetailItem Icon={LocalOfferOutlined}>
                    <Labels
                        selectedLabels={contact.labels}
                        addLabel={addLabel}
                        removeLabel={removeLabel}
                        onLabelEdit={invalidateContact}
                    />
                </DetailItem>
            </CardContent>
        </Card>
    );
};

export default ContactInfoCard;
